import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { APIError } from "@/adapters/api";
import { DatePicker } from "@/components/date-picker";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useIssuedRdosSince } from "@/features/rdo/hooks/use-issued-rdos-since.ts";
import {
	affectedRdosWarning,
	rdosAffectedByMeasurement,
} from "@/features/rdo/utils/issued.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";
import {
	currentWorkDate,
	currentWorkDay,
	dateOnlyToDate,
	dateToDateOnly,
} from "@/lib/format.ts";
import { useStageMeasurement } from "../../hooks/use-stage-measurement.ts";
import { useUpsertMeasurement } from "../../hooks/use-upsert-measurement.ts";
import {
	type MeasurementFormValues,
	measurementSchema,
} from "../../schemas/measurement-schema.ts";
import type { MeasurementTarget } from "../../types.ts";
import { formatMeasuredOn } from "../../utils/format.ts";

const VIOLATION_FIELDS: Record<string, keyof MeasurementFormValues> = {
	measured_on: "measuredOn",
	progress_percent: "progressPercent",
	note: "note",
};

const DESCRIPTION_MESSAGES: Record<string, string> = {
	required: "Campo obrigatório.",
	invalid: "Valor inválido.",
	future: "A data da medição não pode ser futura.",
	"lt:0": "O percentual deve estar entre 0 e 100.",
	"gt:100": "O percentual deve estar entre 0 e 100.",
	not_leaf: "Somente etapas sem sub-etapas podem ser medidas.",
};

function describe(description: string): string {
	return DESCRIPTION_MESSAGES[description] ?? description;
}

function emptyValues(): MeasurementFormValues {
	return { measuredOn: currentWorkDate(), progressPercent: "", note: "" };
}

export function MeasurementDialog({
	open,
	onOpenChange,
	constructionId,
	stage,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	constructionId: string;
	stage: MeasurementTarget | null;
}) {
	const [formError, setFormError] = useState<string | null>(null);
	const upsert = useUpsertMeasurement(constructionId);
	const navigate = useNavigate();
	const { obra: obraKey } = useParams();
	const { has } = usePermissions();

	const {
		control,
		handleSubmit,
		register,
		reset,
		setError,
		setValue,
		watch,
		formState: { errors },
	} = useForm<MeasurementFormValues>({
		resolver: zodResolver(measurementSchema),
		defaultValues: emptyValues(),
	});

	const measuredOn = watch("measuredOn");
	const existing = useStageMeasurement(
		open ? constructionId : undefined,
		stage?.id,
		measuredOn,
	);

	const issuedRdos = useIssuedRdosSince(
		open ? constructionId : undefined,
		measuredOn,
		has("rdos:list"),
	);
	const affectedRdos = rdosAffectedByMeasurement(
		issuedRdos.data ?? [],
		measuredOn,
	);
	const affectedWarning = affectedRdosWarning(affectedRdos);

	const appliedDayRef = useRef<string | null>(null);

	useEffect(() => {
		if (!open) {
			appliedDayRef.current = null;
			return;
		}
		reset(emptyValues());
		setFormError(null);
	}, [open, reset]);

	useEffect(() => {
		if (!open || existing.isPending || existing.isError) return;
		if (appliedDayRef.current === measuredOn) return;
		appliedDayRef.current = measuredOn;
		const found = existing.data;
		setValue("progressPercent", found ? String(found.progressPercent) : "");
		setValue("note", found?.note ?? "");
	}, [
		open,
		measuredOn,
		existing.data,
		existing.isPending,
		existing.isError,
		setValue,
	]);

	const isEditing = !!existing.data;

	function applyAPIError(err: unknown, fallback: string) {
		if (err instanceof APIError) {
			let matchedField = false;
			for (const [field, description] of Object.entries(err.violations)) {
				const formField = VIOLATION_FIELDS[field];
				if (formField) {
					setError(formField, { message: describe(description) });
				} else {
					setFormError(describe(description));
				}
				matchedField = true;
			}
			if (!matchedField) setFormError(err.message || fallback);
			return;
		}
		setFormError(err instanceof Error ? err.message : fallback);
	}

	async function onSubmit(values: MeasurementFormValues) {
		if (!stage) return;
		setFormError(null);
		try {
			await upsert.mutateAsync({
				stageId: stage.id,
				input: {
					measuredOn: values.measuredOn,
					progressPercent: Number(values.progressPercent),
					note: values.note.trim(),
				},
			});
			const saved = isEditing
				? "Medição atualizada!"
				: "Medição lançada com sucesso!";
			const [firstAffected] = affectedRdos;
			if (firstAffected) {
				toast.warning(saved, {
					description: affectedWarning,
					duration: 10_000,
					action: obraKey
						? {
								label: "Abrir RDO",
								onClick: () => navigate(`/${obraKey}/rdo/${firstAffected.id}`),
							}
						: undefined,
				});
			} else {
				toast.success(saved);
			}
			onOpenChange(false);
		} catch (err) {
			applyAPIError(err, "Não foi possível salvar a medição.");
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{isEditing ? "Corrigir medição" : "Lançar medição"}
					</DialogTitle>
					<DialogDescription>
						{stage
							? `Percentual executado de “${stage.name}” na data informada.`
							: ""}
					</DialogDescription>
				</DialogHeader>
				<form
					id="measurement-form"
					className="measurement-form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<div className="measurement-form__fields">
						<div className="form-group">
							<Label htmlFor="measurement-percent">Executado (%)</Label>
							<Input
								id="measurement-percent"
								type="number"
								inputMode="numeric"
								min={0}
								max={100}
								step={1}
								placeholder="0"
								aria-invalid={!!errors.progressPercent}
								{...register("progressPercent")}
							/>
							{errors.progressPercent && (
								<p className="form-error">{errors.progressPercent.message}</p>
							)}
						</div>
						<div className="form-group">
							<Label htmlFor="measurement-date">Data da medição</Label>
							<Controller
								control={control}
								name="measuredOn"
								render={({ field }) => (
									<DatePicker
										id="measurement-date"
										name={field.name}
										modal
										selected={dateOnlyToDate(field.value)}
										onSelected={(date) => field.onChange(dateToDateOnly(date))}
										aria-invalid={!!errors.measuredOn}
										calendarProps={{ disabled: { after: currentWorkDay() } }}
									/>
								)}
							/>
							{errors.measuredOn && (
								<p className="form-error">{errors.measuredOn.message}</p>
							)}
						</div>
					</div>

					<div className="form-group">
						<Label htmlFor="measurement-note">Nota (opcional)</Label>
						<Textarea
							id="measurement-note"
							rows={3}
							placeholder="O que foi executado, impedimentos, observações…"
							aria-invalid={!!errors.note}
							{...register("note")}
						/>
						{errors.note && <p className="form-error">{errors.note.message}</p>}
					</div>

					{isEditing && (
						<p className="measurement-form__hint">
							Já existe uma medição de {existing.data?.progressPercent}% em{" "}
							{formatMeasuredOn(measuredOn)}. Salvar substitui o valor do dia.
						</p>
					)}

					{affectedWarning && (
						<div role="status" className="measurement-form__warning">
							<TriangleAlert aria-hidden />
							<p>{affectedWarning}</p>
						</div>
					)}

					{formError && (
						<p role="alert" className="form-error-alert">
							{formError}
						</p>
					)}
				</form>
				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={upsert.isPending}
					>
						Cancelar
					</Button>
					<Button
						type="submit"
						form="measurement-form"
						disabled={upsert.isPending || existing.isPending}
					>
						{upsert.isPending && (
							<Loader2 className="measurement-form__spinner" />
						)}
						{isEditing ? "Atualizar medição" : "Salvar medição"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
